import { defineStore } from 'pinia';
import { shallowRef, toRaw } from 'vue';
const defaultState = {
    farm: null,
    view: 'apps',
    viewOfflineOnline: 'online',
    server: null,
    lastServerID: null,
    dataLoaded: false,
    devices: [],
    computedDevices: [],
    deviceStatuses: {},
    minerTypes: [],
    selectedMinerType: null,
    boardTypes: [],
    selectedBoardType: null,
    sortBy: 'ip',
    sortDir: 'asc',
    selectedIPs: [],
    selectedIDs: [],
    stateSelectedAllDevices: false,
    tabMain: 'connect',
    tabPage: 'network'
};
export const useFarmStore = defineStore('farm', {
    state: () => ({
        farm: null,
        view: 'apps',
        viewOfflineOnline: 'online',
        server: null,
        lastServerID: null,
        dataLoaded: false,
        devices: shallowRef([]),
        computedDevices: shallowRef([]),
        deviceStatuses: shallowRef({}),
        minerTypes: shallowRef([]),
        selectedMinerType: null,
        boardTypes: shallowRef([]),
        selectedBoardType: null,
        sortBy: 'ip',
        sortDir: 'asc',
        selectedIPs: [],
        selectedIDs: [],
        stateSelectedAllDevices: false,
        tabMain: 'connect',
        tabPage: 'network'
    }),
    getters: {},
    actions: {
        recomputeComputedDevices() {
            const devices = structuredClone(toRaw(this.devices ?? []));
            const minerTypes = [...new Set(devices.map((d) => d?.miner_type).filter(Boolean))].sort();
            const boardTypes = [...new Set(devices.map((d) => d?.miner_board_type).filter(Boolean))].sort();
            this.minerTypes = minerTypes;
            if (this.selectedMinerType !== null && !minerTypes.includes(this.selectedMinerType)) {
                this.selectedMinerType = null;
            }
            this.boardTypes = boardTypes;
            if (this.selectedBoardType !== null && !boardTypes.includes(this.selectedBoardType)) {
                this.selectedBoardType = null;
            }
            let computed = devices;
            if (this.selectedMinerType) {
                computed = computed.filter((d) => d?.miner_type === this.selectedMinerType);
            }
            if (this.selectedBoardType) {
                computed = computed.filter((d) => d?.miner_board_type === this.selectedBoardType);
            }
            const column = this.sortBy === 'ip' ? 'ipNum' : this.sortBy;
            computed.sort((a, b) => {
                if (a[column] < b[column])
                    return this.sortDir === 'asc' ? -1 : 1;
                if (a[column] > b[column])
                    return this.sortDir === 'asc' ? 1 : -1;
                return 0;
            });
            this.computedDevices = computed;
        },
        setCurrentServerOnline(serverID, online) {
            if (!this.server || this.server.id !== serverID)
                return;
            this.server.online = online;
            this.dataLoaded = true;
            if (!online) {
                this.selectedIPs = [];
                this.stateSelectedAllDevices = false;
            }
        },
        applyServerDevicesSnapshot(serverID, devicesOrDone, fromScan = false) {
            if (!this.server || this.server.id !== serverID)
                return;
            this.server.online = true;
            if (devicesOrDone === 'done') {
                this.dataLoaded = true;
                return;
            }
            this.devices = devicesOrDone;
            this.recomputeComputedDevices();
            if (!fromScan) {
                this.dataLoaded = true;
            }
        },
        applyFarmDevicesSnapshot(farmID, devices) {
            if (!this.farm || this.farm.id !== farmID)
                return;
            const prevDevices = toRaw(this.devices ?? []);
            const deviceStatuses = structuredClone(toRaw(this.deviceStatuses ?? {}));
            for (const device of devices) {
                const id = device?.id;
                if (id === null || id === undefined)
                    continue;
                const prev = prevDevices.find((d) => d?.id === id);
                if (prev?.updatingFirmware && !device?.updatingFirmware) {
                    deviceStatuses[id] = {
                        label: 'Done',
                        color: 'primary',
                        removeOnClick: true
                    };
                }
            }
            this.deviceStatuses = deviceStatuses;
            this.devices = devices;
            this.recomputeComputedDevices();
        },
        applyNetScannerProgress(serverID, progress) {
            if (!this.server || this.server.id !== serverID)
                return;
            const server = this.server;
            if (progress !== 100) {
                server.scanning = true;
                server.scanProgress = progress;
            }
            else {
                server.scanning = false;
                server.scanProgress = 0;
            }
        },
        reset() {
            Object.assign(this.$state, {
                ...defaultState,
                devices: [],
                computedDevices: [],
                deviceStatuses: {},
                minerTypes: [],
                boardTypes: []
            });
        },
        resetAllButFarm() {
            Object.assign(this.$state, {
                view: 'apps',
                server: null,
                dataLoaded: false,
                devices: [],
                computedDevices: [],
                deviceStatuses: {},
                minerTypes: [],
                selectedMinerType: null,
                boardTypes: [],
                selectedBoardType: null,
                selectedIPs: [],
                selectedIDs: [],
                stateSelectedAllDevices: false
            });
        }
    },
});
